/**
 * WordCloud — TELV1-332 (concept-cloud reframe).
 *
 * Renders a word/concept cloud built on @visx/wordcloud (d3-cloud). The SVG is
 * decorative; a visually-hidden ordered list serves screen readers.
 *
 * Originally drove off raw word frequencies; now accepts a richer item
 * shape — each item can carry an optional `sentiment` tag that maps to a
 * semantic colour, and an optional click handler that the page wires to
 * an evidence filter ("show me the respondents behind this concept").
 *
 * Design decisions:
 * - Font size: sqrt-scaled 12–36 px so frequent items are large but don't
 *   dominate overwhelmingly.
 * - Rotation: always 0° — Dutch words are long; rotation hurts readability.
 * - Palette:
 *   - When `sentiment` is provided: semantic palette
 *       positive  → brand green   (#096846, contrast 6.82:1 on white)
 *       negative  → darkened pink (#B83060, contrast 5.79:1)
 *       mixed     → midpoint      (#7A5060, blend of the two)
 *       neutral   → neutral dark  (#2A3547, contrast 12.36:1)
 *   - When `sentiment` is absent: brand palette cycled by rank (backward
 *     compatible — keeps any pre-reframe callers working).
 * - Single-dominant guard: if the top item owns >60% of the total weight,
 *   its font size is capped at 70% of the maximum so it doesn't swamp the cloud.
 * - Weight 600 for the top 5 items, 400 otherwise (size already encodes prevalence).
 * - Max 40 items rendered (top by value).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import Wordcloud from '@visx/wordcloud/lib/Wordcloud';
import { ParentSize } from '@visx/responsive';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';

// ── Semantic palette (sentiment-aware) — WCAG AA on white ────────────────
const SENTIMENT_COLORS = {
  positive: '#096846', // brand green     contrast 6.82:1
  negative: '#B83060', // darkened pink   contrast 5.79:1
  mixed: '#7A5060', // blend
  neutral: '#2A3547', // neutral dark    contrast 12.36:1
} as const;

// ── Rank-cycled brand palette (backward-compatible fallback) ─────────────
const RANK_PALETTE = ['#096846', '#075539', '#2A3547', '#B83060'] as const;

const MAX_WORDS = 40;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 36;
const SINGLE_DOMINANT_THRESHOLD = 0.6;
const SINGLE_DOMINANT_CAP_RATIO = 0.7;
const TOP_N_BOLD = 5;

export type WordCloudSentiment = 'positive' | 'negative' | 'neutral' | 'mixed';

/**
 * One item rendered in the cloud. `text` and `value` are required —
 * everything else is optional metadata used for color, tooltip, click.
 */
export interface WordCloudItem {
  text: string;
  value: number;
  sentiment?: WordCloudSentiment;
  /** Pre-built tooltip override; defaults to "<text>: <value> mentions". */
  tooltip?: string;
  /** Arbitrary marker the parent can use to filter/highlight. */
  payload?: unknown;
}

/** Internal — adds the layout fields produced by d3-cloud. */
interface ComputedWord extends WordCloudItem {
  fontSize: number;
  x?: number;
  y?: number;
  rotate?: number;
  size?: number;
}

interface WordCloudProps {
  words: WordCloudItem[];
  /** Height of the SVG container in pixels. Default: 220. */
  height?: number;
  /** Maximum number of words to render. Default: 40. */
  maxWords?: number;
  /** Click handler — invoked with the original item. */
  onWordClick?: (item: WordCloudItem) => void;
}

// Stable font-size accessor passed to <Wordcloud>.
function fontSizeAccessor(word: ComputedWord): number {
  return word.fontSize ?? MIN_FONT_SIZE;
}

// Rotation: always 0° — no angle needed.
function rotateWord(): number {
  return 0;
}

function resolveColor(item: WordCloudItem, rank: number): string {
  if (item.sentiment) return SENTIMENT_COLORS[item.sentiment];
  return RANK_PALETTE[rank % RANK_PALETTE.length];
}

function WordCloudInner({
  words,
  width,
  height,
  onWordClick,
}: {
  words: WordCloudItem[];
  width: number;
  height: number;
  onWordClick?: (item: WordCloudItem) => void;
}) {
  const sliced = useMemo(() => words.slice(0, MAX_WORDS), [words]);

  const maxFreq = useMemo(() => (sliced.length ? sliced[0].value : 1), [sliced]);
  const totalFreq = useMemo(() => sliced.reduce((acc, w) => acc + w.value, 0), [sliced]);

  // Determine whether the dominant word should be capped.
  const dominantRatio = sliced.length ? sliced[0].value / totalFreq : 0;
  const isDominant = dominantRatio > SINGLE_DOMINANT_THRESHOLD;

  const computedWords = useMemo<ComputedWord[]>(
    () =>
      sliced.map((w, i) => {
        const rawSize =
          MIN_FONT_SIZE + Math.sqrt(w.value / maxFreq) * (MAX_FONT_SIZE - MIN_FONT_SIZE);
        const fontSize =
          i === 0 && isDominant
            ? Math.min(rawSize, MAX_FONT_SIZE * SINGLE_DOMINANT_CAP_RATIO)
            : rawSize;
        return { ...w, fontSize };
      }),
    [sliced, maxFreq, isDominant],
  );

  if (!computedWords.length) {
    return <p className="text-sm text-neutral-400 text-center py-6">No concept data yet</p>;
  }

  return (
    <>
      {/* Decorative SVG — hidden from screen readers. */}
      <svg aria-hidden="true" width={width} height={height} style={{ overflow: 'visible' }}>
        <Wordcloud
          words={computedWords}
          width={width}
          height={height}
          fontSize={fontSizeAccessor}
          font="Inter, sans-serif"
          padding={4}
          rotate={rotateWord}
          spiral="rectangular"
          random={() => 0.5}
        >
          {(cloudWords: any[]) =>
            (cloudWords as ComputedWord[]).map((w, i) => {
              const color = resolveColor(w, i);
              const isBold = i < TOP_N_BOLD;
              const tooltip = w.tooltip ?? `${w.text}: ${w.value} mentions`;
              const interactive = typeof onWordClick === 'function';

              return (
                <TooltipWrapper key={w.text} text={tooltip}>
                  <text
                    textAnchor="middle"
                    transform={`translate(${w.x ?? 0}, ${w.y ?? 0}) rotate(${w.rotate ?? 0})`}
                    fontSize={w.size ?? w.fontSize ?? MIN_FONT_SIZE}
                    fontFamily="Inter, sans-serif"
                    fontWeight={isBold ? 600 : 400}
                    fill={color}
                    style={{
                      cursor: interactive ? 'pointer' : 'default',
                      transition: 'opacity 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as SVGTextElement).style.opacity = '0.75';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as SVGTextElement).style.opacity = '1';
                    }}
                    onClick={interactive ? () => onWordClick?.(w) : undefined}
                  >
                    {w.text}
                  </text>
                </TooltipWrapper>
              );
            })
          }
        </Wordcloud>
      </svg>

      {/* Accessible ranked list — visually hidden, read by screen readers. */}
      <ol className="sr-only" aria-label="Top items by prevalence">
        {computedWords.map((w) => (
          <li key={w.text}>
            {w.text}: {w.value} mentions
          </li>
        ))}
      </ol>
    </>
  );
}

function WordCloud({ words, height = 220, maxWords = MAX_WORDS, onWordClick }: WordCloudProps) {
  const slicedWords = useMemo(
    () => [...words].sort((a, b) => b.value - a.value).slice(0, maxWords),
    [words, maxWords],
  );

  if (!slicedWords.length) {
    return (
      <div className="flex items-center justify-center py-6">
        <p className="text-sm text-neutral-400">No concept data yet</p>
      </div>
    );
  }

  return (
    <ParentSize debounceTime={100}>
      {(parent) => {
        // `parent.width` is typed by @visx/responsive's ParentSize children
        // signature. We avoid destructuring an inline-typed `{ width }` here
        // because react/no-unused-prop-types misreads the inline type literal
        // as an unused component prop (false positive on render props).
        if (parent.width < 1) return null;
        return (
          <WordCloudInner
            words={slicedWords}
            width={parent.width}
            height={height}
            onWordClick={onWordClick}
          />
        );
      }}
    </ParentSize>
  );
}

export default WordCloud;
